/**
 * Logger for staging and production environment.
 *
 * Author: Bounlith Sirivong <bounlith.sirivong@ircamamplify.com>
 * Copyright © Ircam Amplify 2024-2025
 */

/**
 * Tags enabling the logs.
 *
 * This tags could be specified in the variable VITE_LOG_TAG of the file .env.
 * The enums must be prefixed with #.
 * Tags defined in VITE_LOG_TAG variable must be specified without #,
 * separated by a comma.
 */
export enum LogTag {
  BillingPage = '#billing-page',
  FileUploader = '#file-uploader',
  HomePage = '#home-page',
  Module = '#module',
  TaskCreationPage = '#task-creation-page',
  TaskList = '#task-list',
  Util = '#util',
}

let cachedUri: string | undefined

export const getApiBaseUri = (): string => {
  if (cachedUri) return cachedUri

  if (
    typeof import.meta !== 'undefined' &&
    import.meta.env?.VITE_API_BASE_URI
  ) {
    cachedUri = import.meta.env.VITE_API_BASE_URI
    return cachedUri ?? ''
  }

  if (typeof process !== 'undefined' && process.env?.VITE_API_BASE_URI) {
    cachedUri = process.env.VITE_API_BASE_URI
    return cachedUri ?? ''
  }

  throw new Error('VITE_API_BASE_URI is not defined')
}

export const isEnvStaging = getApiBaseUri().includes('api.staging')

export const isEnvDevelopment = getApiBaseUri().includes('api.dev')

export const isEnvProduction = !isEnvStaging && !isEnvDevelopment

const getLocalTimestamp = () => {
  const now = new Date()
  return (
    `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')} ` +
    `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}:${String(now.getSeconds()).padStart(2, '0')}.${String(now.getMilliseconds()).padStart(3, '0')}`
  )
}

/**
 * Return the name of the function caller of a log function.
 *
 * @returns A string containing the file name, the line and the column
 *  of the caller.
 */
const getCallerFileName = (): string => {
  const stack = new Error().stack
  if (!stack) return 'unknown'

  // Match all URLs, skipping the first one or two occurrences
  const matches = stack.match(/https?:\/\/[^\s]+/g)

  if (matches) {
    // Ignore the first match (the logger file itself) and return the next one
    for (const match of matches) {
      if (!match.includes('IampLogger.tsx')) {
        const cleanedMatch = match.replace(/(.*)\?(.*):([\d]*):([\d]*)/, '$1')

        // Extract only the file name
        return cleanedMatch.split('/').pop() || 'unknown'
      }
    }
  }

  return 'unknown'
}

const getPrefixMessage = (
  prefix: string,
  callerFileName: string,
  message?: string
): string => {
  return `${getLocalTimestamp()} [${prefix}] [${callerFileName}] ${message}`
}

/**
 * Return True if the tag is in the variable VITE_LOG_TAG in .env.
 *
 * Tags defined in import.meta.env.VITE_LOG_TAG variable must specified
 * without #, separated by a comma.
 *
 * If The variable import.meta.env.VITE_LOG_TAG is not defined,
 * always return true.
 *
 * @param rawTag
 * @returns True of false.
 */
const isTagEnabled = (rawTag: LogTag) => {
  if (!import.meta.env.VITE_LOG_TAG) {
    // No Vite variable to filter on log tags.
    // Enable all logs by default.
    return true
  }
  const tag = rawTag.startsWith('#') ? rawTag.slice(1) : rawTag
  const enabledTags = import.meta.env.VITE_LOG_TAG?.split(',') ?? []
  return enabledTags.includes(tag)
}

/**
 * The types of log levels.
 */
type LogLevel = 'DEBUG' | 'INFO' | 'WARNING' | 'ERROR'

/**
 * Log function.
 *
 * @param level
 *    The log level.
 * @param tagOrMessage
 *    If defined, filter with the #-prefixed tag to enable the log.
 * @param rest
 *    The rest of the message argument.
 *
 * If the argument `tagOrMessage` is given, the value is searched in the Vite
 * environment variable VITE_LOG_TAG without its # prefix. If the tag is found,
 * the log is enabled.
 * Tags defined in VITE_LOG_TAG variable must be specified without #,
 * separated by a comma.
 * If the variable VITE_LOG_TAG is not defined, all logs are enabled and
 * tagOrMessage is useless.
 *
 * If the argument `tagOrMessage` is not given, all the arguments after `level`
 * are supposed to be part of the message.
 *
 * Example:
 *
 * - Tagged (home-page), enabled if in .env:
 *    log('DEBUG', LogTag.HomePage, 'x:', x)
 *
 * - Regular message, always in dev or staging:
 *    log('DEBUG', 'Variable without tag:', x)
 *
 * - Message only, treated as normal message:
 *    log('DEBUG', '#some_string')
 */
const log = (level: LogLevel, tagOrMessage?: LogTag | any, ...rest: any[]) => {
  // Always disable in Production environment
  if (isEnvProduction) return

  let message: any
  let optionalParams: any[]

  const isValidTag = Object.values(LogTag).includes(tagOrMessage)

  if (isValidTag && rest.length > 0) {
    if (!isTagEnabled(tagOrMessage)) return
    ;[message, ...optionalParams] = rest
  } else {
    ;[message, ...optionalParams] = [tagOrMessage, ...rest]
  }

  const prefix = getPrefixMessage(level, getCallerFileName(), message)

  switch (level) {
    case 'WARNING':
      console.warn(prefix, ...optionalParams)
      break
    case 'ERROR':
      console.error(prefix, ...optionalParams)
      break
    default:
      console.log(prefix, ...optionalParams)
  }
}

/**
 * Logger object displaying logs only in non-production environment.
 *
 * Logs are enabled only in Staging or Development enviroment.
 * If a LogTag is specified as the first argument of one of the log functions,
 * the log is enabled only if the tag is in the variable VITE_LOG_TAG in .env.
 *
 * Tags defined in VITE_LOG_TAG variable must be specified without #,
 * separated by a comma.
 *
 * Examples of usage:
 *
 * - Tagged (home-page), enabled if in .env:
 *    IampLogger.debug(LogTag.HomePage, 'x:', x)
 *
 * - Regular message, always in dev or staging:
 *    IampLogger.debug('Variable without tag:', x)
 *
 * - Message only, treated as normal message:
 *    IampLogger.debug('#some_string')
 */
export const IampLogger = {
  debug: (...args: any[]) => log('DEBUG', ...args),
  info: (...args: any[]) => log('INFO', ...args),
  warning: (...args: any[]) => log('WARNING', ...args),
  error: (...args: any[]) => log('ERROR', ...args),
}
